import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Grid, MenuItem, Paper, TextField } from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { FunilEtapas } from '@/components/dashboard/FunilEtapas'
import { GraficoColunas } from '@/components/dashboard/GraficoColunas'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import { formatBRLCurto } from '@/components/dashboard/canais'
import { curadoriaService } from '@/services/curadoriaService'
import { dashboardService } from '@/services/dashboardService'
import { notificacaoService } from '@/services/notificacaoService'
import { CuradoriaResumo, ReservasResumo } from '@/types/curadoria'
import { DashboardResumo } from '@/types/dashboard'
import { NotificacaoResumo } from '@/types/notificacao'
import { CANAIS_COMERCIAIS, CANAL_LABEL } from '@/types/statusReserva'
import { canalStore } from '@/store/canalStore'
import { tokens } from '@/theme'
import { metaPara } from './alertasMeta'

const RAMPA = ['#F9B79A', tokens.brandPrimaryLight, tokens.brandPrimary, tokens.brandPrimaryDark]

/**
 * Dashboard do operador (conceito BI, 11/09): a fila de curadoria em quatro KPIs, o
 * funil de reservas por canal em colunas verticais, os resumos de reservas e do
 * financeiro e os alertas recentes. Substitui o dashboard legado para OPERADOR
 * (`DashboardPage`); ADMIN/GERENTE seguem na Visão de Carteira.
 *
 * Fontes (o Dashboard não tem número próprio):
 *  - KPIs ← `GET /v1/oportunidades/curadoria` (resumo da fila — mesma fonte de
 *    "Reservas e Curadoria");
 *  - funil / financeiro ← `GET /v1/dashboard/resumo?canal=` (mesmo agregado do Kanban);
 *  - reservas ← `GET /v1/oportunidades/resumo` (mesma fonte de Minhas Reservas);
 *  - alertas ← notificações recentes.
 */
export const DashboardOperador: FC = () => {
  const navigate = useNavigate()
  const canalGlobal = canalStore((st) => st.canal)

  const [fila, setFila] = useState<CuradoriaResumo | null>(null)
  const [reservas, setReservas] = useState<ReservasResumo | null>(null)
  const [alertas, setAlertas] = useState<NotificacaoResumo[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [canal, setCanal] = useState<string>('')
  useEffect(() => {
    setCanal(canalGlobal)
  }, [canalGlobal])
  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  const [carregandoResumo, setCarregandoResumo] = useState(true)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    const [f, r, a] = await Promise.allSettled([
      curadoriaService.fila(),
      curadoriaService.resumoReservas(),
      notificacaoService.recentes(5),
    ])
    if (f.status === 'fulfilled') {
      setFila(f.value.resumo)
    } else {
      const e = f.reason
      setErro(e instanceof Error ? e.message : 'Falha ao carregar a fila de curadoria')
    }
    setReservas(r.status === 'fulfilled' ? r.value : null)
    setAlertas(a.status === 'fulfilled' ? a.value : [])
    setCarregando(false)
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  useEffect(() => {
    let ativo = true
    setCarregandoResumo(true)
    dashboardService
      .getResumo(canal || undefined)
      .then((r) => {
        if (ativo) setResumo(r)
      })
      .catch(() => {
        if (ativo) setResumo(null)
      })
      .finally(() => {
        if (ativo) setCarregandoResumo(false)
      })
    return () => {
      ativo = false
    }
  }, [canal])

  const financeiro = resumo?.financeiro

  return (
    <Box>
      <PageHeader
        title="Operação"
        eyebrow="Principal"
        subtitle="Fila de curadoria, funil de reservas e financeiro"
        acao={
          <Button variant="contained" onClick={() => navigate('/reservas-curadoria')}>
            Ver fila de curadoria
          </Button>
        }
      />

      {erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem="Não foi possível carregar a fila de curadoria"
          detalhe={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : (
        <>
          {/* KPIs da fila — mesma fonte de "Reservas e Curadoria". */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                variante="escura"
                label="Novas na fila"
                valor={fila?.novas ?? '—'}
                legenda="Enviadas, ainda sem análise"
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                variante="marca"
                label="Em curadoria"
                valor={fila?.emCuradoria ?? '—'}
                legenda="Em análise ou com pendência"
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Liberadas"
                valor={fila?.liberadas ?? '—'}
                legenda={fila ? `Últimos ${fila.janelaLiberadasDias} dias` : undefined}
                carregando={carregando}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Rascunhos"
                valor={fila?.rascunhos ?? '—'}
                legenda={fila ? `${fila.excecoes} exceções em aberto` : undefined}
                carregando={carregando}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Funil de reservas"
                  descricao="Reservas por etapa comercial e o valor em cada uma. Clique para abrir o Kanban."
                  acao={
                    // Comparação por canal (6.7): "Sem canal" = reservas anteriores ao carimbo.
                    <TextField
                      select
                      size="small"
                      label="Canal"
                      value={canal}
                      onChange={(e) => setCanal(e.target.value)}
                      sx={{ minWidth: 220 }}
                      inputProps={{ 'aria-label': 'Filtrar por canal comercial' }}
                    >
                      <MenuItem value="">Todos os canais</MenuItem>
                      {CANAIS_COMERCIAIS.map((c) => (
                        <MenuItem key={c} value={c}>
                          {CANAL_LABEL[c]}
                        </MenuItem>
                      ))}
                      <MenuItem value="SEM_CANAL">Sem canal (reservas antigas)</MenuItem>
                    </TextField>
                  }
                />
                {carregandoResumo ? (
                  <EstadoDeLista estado="carregando" minHeight={200} />
                ) : resumo ? (
                  <FunilEtapas
                    porEtapa={resumo.pipeline.porEtapaComercial}
                    valorPorEtapa={resumo.pipeline.valorPorEtapaComercial}
                    aoClicar={() => navigate('/kanban')}
                    altura={150}
                  />
                ) : (
                  <EstadoDeLista estado="vazio" mensagem="Sem dados no momento" minHeight={200} />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Reservas"
                  descricao="Mesmos números de Minhas Reservas."
                  acao={
                    <Button size="small" onClick={() => navigate('/reservas')}>
                      Ver reservas
                    </Button>
                  }
                />
                {carregando ? (
                  <EstadoDeLista estado="carregando" minHeight={160} />
                ) : reservas ? (
                  <GraficoColunas
                    altura={150}
                    itens={[
                      { chave: 'ativas', rotulo: 'Ativas', valor: reservas.ativas, cor: RAMPA[2] },
                      {
                        chave: 'aVencer',
                        rotulo: 'A vencer',
                        valor: reservas.aVencer,
                        cor: RAMPA[3],
                      },
                      {
                        chave: 'emEstruturacao',
                        rotulo: 'Em estruturação',
                        valor: reservas.emEstruturacao,
                        cor: RAMPA[1],
                      },
                      {
                        chave: 'ganhas',
                        rotulo: 'Ganhas',
                        valor: reservas.ganhas,
                        cor: tokens.brandInk,
                      },
                    ]}
                  />
                ) : (
                  <EstadoDeLista estado="vazio" mensagem="Sem dados no momento" minHeight={160} />
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Financeiro"
                  descricao="Volumes e comissões do recorte — mesma fonte do Financeiro."
                />
                {carregandoResumo ? (
                  <EstadoDeLista estado="carregando" minHeight={160} />
                ) : financeiro ? (
                  <GraficoColunas
                    altura={130}
                    formatoValor={formatBRLCurto}
                    itens={[
                      {
                        chave: 'negociacao',
                        rotulo: 'Em negociação',
                        valor: Number(financeiro.volumeEmNegociacao ?? 0),
                        cor: RAMPA[1],
                      },
                      {
                        chave: 'fechado',
                        rotulo: 'Volume fechado',
                        valor: Number(financeiro.volumeFechado ?? 0),
                        cor: tokens.brandInk,
                      },
                      {
                        chave: 'projetada',
                        rotulo: 'Comissão projetada',
                        valor: Number(financeiro.comissaoProjetada ?? 0),
                        cor: RAMPA[2],
                      },
                      {
                        chave: 'nota',
                        rotulo: 'Disponível p/ nota',
                        valor: Number(financeiro.comissaoDisponivelParaNota ?? 0),
                        cor: RAMPA[3],
                      },
                    ]}
                  />
                ) : (
                  <EstadoDeLista estado="vazio" mensagem="Sem dados no momento" minHeight={160} />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao titulo="Alertas" descricao="Últimas notificações da operação." />
                {carregando ? (
                  <EstadoDeLista estado="carregando" minHeight={160} />
                ) : alertas.length === 0 ? (
                  <EstadoDeLista
                    estado="vazio"
                    mensagem="Nenhum alerta no momento"
                    minHeight={160}
                  />
                ) : (
                  alertas.map((n) => {
                    const meta = metaPara(n.evento)
                    return (
                      <Alert
                        key={n.id}
                        severity={meta.severity}
                        icon={meta.icon}
                        sx={{ mb: 1, alignItems: 'center' }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigate(meta.action)}
                          >
                            Ver
                          </Button>
                        }
                      >
                        {n.titulo}
                      </Alert>
                    )
                  })
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}

export default DashboardOperador
